import type { PeniellyItem, PublicUser } from "@rhema/client";
import {
  Button,
  CameraIcon,
  cn,
  FileTextIcon,
  focusRing,
  HeadphonesIcon,
  ImageIcon,
  MapPinIcon,
  PollIcon,
  ProductMark,
  Sheet,
  UserIcon,
  VideoIcon,
} from "@rhema/ui";
import { type ReactNode, useEffect, useState } from "react";
import { PeniellyPicker } from "../links/PeniellyPicker";
import { PeoplePicker } from "../people/PeoplePicker";
import { type NewPoll, PollComposer } from "./PollComposer";

export type PickSource = "gallery" | "video" | "camera" | "document" | "audio";
type Step = "menu" | "location" | "contact" | "poll" | "penielly";

const OPTIONS: { id: PickSource | Exclude<Step, "menu">; label: string; icon: ReactNode }[] = [
  { id: "gallery", label: "Gallery", icon: <ImageIcon size={25} strokeWidth={1.8} /> },
  { id: "video", label: "Videos", icon: <VideoIcon size={25} strokeWidth={1.8} /> },
  { id: "camera", label: "Camera", icon: <CameraIcon size={25} strokeWidth={1.8} /> },
  { id: "penielly", label: "Penielly", icon: null },
  { id: "document", label: "Document", icon: <FileTextIcon size={25} strokeWidth={1.8} /> },
  { id: "location", label: "Location", icon: <MapPinIcon size={25} strokeWidth={1.8} /> },
  { id: "contact", label: "Contact", icon: <UserIcon size={25} strokeWidth={1.8} /> },
  { id: "poll", label: "Poll", icon: <PollIcon size={25} strokeWidth={2.2} /> },
  { id: "audio", label: "Audio", icon: <HeadphonesIcon size={25} strokeWidth={1.8} /> },
];

const TITLES: Record<Exclude<Step, "menu">, string> = {
  location: "Share your location",
  contact: "Share a contact",
  poll: "New poll",
  penielly: "Photos from Penielly",
};

export function AttachSheet({
  open,
  recipient,
  onClose,
  onPick,
  onLocation,
  onContact,
  onPoll,
  onPeniellySend,
  locating = false,
  penielly = true,
  nativePhotos = false,
}: {
  open: boolean;
  recipient: string;
  onClose: () => void;
  /** Opens the file picker for photos and videos, the camera, documents or audio. */
  onPick: (source: PickSource) => void;
  onLocation: () => void;
  onContact: (person: PublicUser) => void;
  onPoll: (poll: NewPoll) => void;
  /** Sends photos picked from Penielly. They go out as normal encrypted photos. */
  onPeniellySend: (items: PeniellyItem[]) => void;
  locating?: boolean;
  /** Whether Penielly may be used here. A space's admins can turn it off for its channels. */
  penielly?: boolean;
  /**
   * Phones pick photos from their own library, which has no videos, so videos get their own
   * button there. Elsewhere the gallery picker takes both.
   */
  nativePhotos?: boolean;
}) {
  const [step, setStep] = useState<Step>("menu");
  useEffect(() => {
    if (!open) setStep("menu");
  }, [open]);

  const choose = (id: (typeof OPTIONS)[number]["id"]) => {
    if (id === "location" || id === "contact" || id === "poll" || id === "penielly") setStep(id);
    else onPick(id);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      fixed={false}
      title={step === "menu" ? `Share with ${recipient}` : TITLES[step]}
      className="px-5 pb-7"
    >
      {step === "menu" && (
        <div className="mt-[18px] grid grid-cols-4 gap-x-2.5 gap-y-[18px]">
          {OPTIONS.filter((o) => (penielly || o.id !== "penielly") && (nativePhotos || o.id !== "video")).map(
            (o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => choose(o.id)}
                className={cn(
                  "flex cursor-pointer flex-col items-center gap-2 rounded-tile bg-transparent text-ink",
                  focusRing,
                )}
              >
                {o.id === "penielly" ? (
                  <ProductMark product="penielly" size={62} />
                ) : (
                  <span className="flex h-[62px] w-[62px] items-center justify-center rounded-[18px] border border-line bg-surface">
                    {o.icon}
                  </span>
                )}
                <span className="text-[13px] font-semibold">
                  {o.id === "gallery" && nativePhotos ? "Photos" : o.label}
                </span>
              </button>
            ),
          )}
        </div>
      )}

      {step === "location" && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="m-0 text-[15px] leading-normal text-ink-secondary">
            Rhema asks your browser for your position once and sends it to {recipient}, encrypted like any
            other message. It does not keep sharing your location after that.
          </p>
          <Button size="lg" block disabled={locating} onClick={onLocation}>
            {locating ? "Finding your location" : "Share my current location"}
          </Button>
          <Button variant="ghost" onClick={() => setStep("menu")}>
            Back
          </Button>
        </div>
      )}

      {step === "contact" && (
        <div className="flex flex-col">
          <PeoplePicker onPick={onContact} />
          <Button variant="ghost" className="mt-3" onClick={() => setStep("menu")}>
            Back
          </Button>
        </div>
      )}

      {step === "poll" && <PollComposer onSend={onPoll} />}

      {step === "penielly" && (
        <PeniellyPicker recipient={recipient} onSend={onPeniellySend} onBack={() => setStep("menu")} />
      )}
    </Sheet>
  );
}
